import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Search, Trash2, Printer, Plus, UserPlus, Edit2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Bits";
import { Modal, Field, inputCls, primaryBtnCls, ghostBtnCls } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { PrintPreview } from "@/components/PrintDoc";
import { PharmacyInvoiceDoc, TEMPLATES } from "@/components/PharmacyInvoice";
import { PhoneInput } from "@/components/PhoneInput";
import BarcodeScanner, { ScanButton } from "@/components/BarcodeScanner";
import { useAuth } from "@/context/AuthContext";
import { inr, dmy, titleize, today } from "@/lib/format";

const MODES = ["cash", "upi", "card", "netbanking", "cheque", "other"];
const money = (v) => Number(v || 0);
const normalizePackagingCode = (value) => String(value ?? "").trim().toLowerCase().replace(/\s+/g, "_");
const productPackaging = (medicine) => (Array.isArray(medicine?.packaging) ? medicine.packaging : [])
  .filter((entry) => entry && (entry.code || entry.label))
  .map((entry) => ({ ...entry, code: normalizePackagingCode(entry.code || entry.label) }));
const sellFactor = (medicine, unit) => {
  const normalizedUnit = normalizePackagingCode(unit);
  return Number(productPackaging(medicine).find((entry) => entry.code === normalizedUnit)?.factor || 1);
};

const baseUnit = (medicine) => {
  const packaging = productPackaging(medicine);
  if (!packaging.length) return "unit";
  const base = packaging.find((entry) => Number(entry.factor) === 1) || packaging[0];
  return base?.code || "unit";
};

const validSaleUnit = (medicine, unit) => {
  const packaging = productPackaging(medicine);
  if (!packaging.length) return "unit";
  const normalizedUnit = normalizePackagingCode(unit);
  if (packaging.some((entry) => entry.code === normalizedUnit)) return normalizedUnit;
  return baseUnit(medicine);
};

const stockHint = (medicine, batch) => {
  const base = Number(batch?.quantity || 0);
  const perStrip = Number(medicine?.tablets_per_strip || 0);
  const strips = perStrip ? Math.floor(base / perStrip) : 0;
  const remainder = perStrip ? base % perStrip : 0;
  if (!perStrip) return `${base} base units available`;
  return `${base} tablets on hand · ${strips} full strips${remainder ? ` + ${remainder} tablets` : ""}`;
};

export default function Pos() {
  const { can } = useAuth();
  // B.9: monotonically increasing search token; only the newest search may
  // commit results (see search below).
  const searchSeq = useRef(0);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null);
  const [qty, setQty] = useState("1");
  const [saleUnit, setSaleUnit] = useState("unit");
  
  const [editingIdx, setEditingIdx] = useState(null);
  const [editingQty, setEditingQty] = useState("1");
  const [cart, setCart] = useState([]);
  const [customer, setCustomer] = useState({ customer_id: "", name: "", phone: "" });
  const [referrals, setReferrals] = useState([]);
  const [referralId, setReferralId] = useState("");
  const [custOpen, setCustOpen] = useState(false);
  const [custResults, setCustResults] = useState([]);

  const searchCustomers = useCallback(async (value) => {
    if ((value || "").trim().length < 2) return setCustResults([]);
    try {
      const { data } = await api.get("/pharmacy/customers", { params: { q: value.trim() } });
      setCustResults(data.items);
    } catch { setCustResults([]); }
  }, [setCustResults]);
  const [custForm, setCustForm] = useState({ name: "", phone: "" });
  const [meta, setMeta] = useState({ doctor_name: "", invoice_date: today(), due_date: "", notes: "" });
  const [discountMode, setDiscountMode] = useState("amount");
  const [billDiscount, setBillDiscount] = useState("0");
  const [payments, setPayments] = useState([{ mode: "cash", amount: "", reference: "" }]);
  const [terms, setTerms] = useState("");
  const [template, setTemplate] = useState("classic");
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");
  const [preview, setPreview] = useState(null);
  const [scanOpen, setScanOpen] = useState(false);

  // Add a scanned barcode to the cart. Same barcode again → increment qty.
  const handleBarcode = useCallback(async (code) => {
    try {
      const { data } = await api.get("/pharmacy/medicines/by-barcode", { params: { code } });
      const med = data.medicine;
      if (!med) {
        toast.error(`No product with barcode ${code} in your inventory.`);
        return;
      }
      const batch = (med.batches || [])[0];
      if (!batch) {
        toast.error(`${med.name} has no sellable stock.`);
        return;
      }
      setCart((prev) => {
        const existing = prev.findIndex((l) => l.batch_id === batch.batch_id);
        if (existing >= 0) {
          const line = prev[existing];
          if (line.quantity + 1 > (line.available || batch.quantity)) {
            toast.error(`Only ${line.available || batch.quantity} units available.`);
            return prev;
          }
          const nq = line.quantity + 1;
          const gross = line.rate * nq;
          const taxable = gross;
          return prev.map((l, i) => i === existing
            ? { ...l, quantity: nq, taxable, gst: taxable * l.gst_percent / 100,
                line_total: taxable + taxable * l.gst_percent / 100 }
            : l);
        }
        const rate = batch.selling_price || batch.mrp;
        const taxable = rate * 1;
        return [...prev, { batch_id: batch.batch_id, batch_no: batch.batch_no, name: med.name,
          expiry_date: batch.expiry_date, mrp: batch.mrp, rate, quantity: 1,
          available: batch.quantity, sale_unit: baseUnit(med), gst_percent: batch.gst_percent, discount: 0,
          taxable, gst: taxable * batch.gst_percent / 100,
          line_total: taxable + taxable * batch.gst_percent / 100 }];
      });
      toast.success(`${med.name} added.`);
    } catch (e) {
      toast.error(apiError(e) || "Barcode lookup failed.");
    }
  }, []);

  useEffect(() => {
    api.get("/pharmacy/invoice-settings")
      .then(({ data }) => { setTemplate(data.template || "classic"); setTerms(data.terms || ""); })
      .catch(() => {});
    api.get("/referrals", { params: { status: "active" } })
      .then(({ data }) => setReferrals(data.items)).catch(() => {});
  }, []);

  useEffect(() => {
    setSaleUnit(picked ? baseUnit(picked) : "unit");
  }, [picked]);

  const search = useCallback(async (value) => {
    setQ(value);
    if (value.trim().length < 2) return setResults([]);
    // B.9: debounce — only the latest keystroke fires. The token captured
    // here is compared when the round-trip returns; any older (slower)
    // response is discarded so stale results can never overwrite fresh ones.
    const token = ++searchSeq.current;
    await new Promise((r) => setTimeout(r, 280));
    if (token !== searchSeq.current) return;
    try {
      // Query both tenant medicines (with batches) and the global master catalog.
      const term = value.trim();
      const [tenantRes, masterRes] = await Promise.allSettled([
        api.get("/pharmacy/medicines/search", { params: { q: term } }),
        api.get("/pharmacy/medicines/master/search", { params: { q: term } }),
      ]);
      if (token !== searchSeq.current) return; // stale round-trip — drop it
      const tenantItems = tenantRes.status === "fulfilled" ? (tenantRes.value.data.items || []) : [];
      const masterItems = masterRes.status === "fulfilled" ? (masterRes.value.data.items || []) : [];
      // Mark master items so the UI can show an "Import" action.
      const masters = masterItems.map((m) => ({ ...m, _is_master: true }));
      setResults([...tenantItems, ...masters]);
    } catch { if (token === searchSeq.current) setResults([]); }
  }, [setResults]);

  const totals = useMemo(() => {
    const subtotal = cart.reduce((s, l) => s + l.taxable, 0);
    const gst = cart.reduce((s, l) => s + l.gst, 0);
    const itemDisc = cart.reduce((s, l) => s + l.discount, 0);
    const gross = subtotal + gst;
    const bd = discountMode === "percent"
      ? gross * money(billDiscount) / 100 : Math.min(money(billDiscount), gross);
    const net = gross - bd;
    const grand = Math.round(net);
    const collected = payments.reduce((s, p) => s + money(p.amount), 0);
    return { subtotal, gst, itemDisc, billDisc: bd, net, roundOff: grand - net, grand,
      collected, balance: grand - collected };
  }, [cart, billDiscount, discountMode, payments]);

  if (!can("pharmacy.sales.create")) return <NoAccess what="the POS counter" />;

  const addLine = () => {
    if (!picked) { toast.error("Search and select a product first."); return; }
    // B.9: a global catalogue suggestion is NOT tenant inventory — it has no
    // batches and no batch_id, so it can never be billed. The server enforces
    // the same rule (batch must exist in this business); this is the UI guard.
    if (picked._is_master) { toast.error(`${picked.name} is a catalogue suggestion — import it to your business before billing.`); return; }
    const n = Number(qty || 0);
    if (n <= 0) { toast.error("Quantity must be at least 1."); return; }
    const batch = picked.batches?.[0];
    if (!batch) { toast.error(`${picked.name} has no stock in a sellable batch.`); return; }
    const selectedUnit = validSaleUnit(picked, saleUnit);
    const factor = sellFactor(picked, selectedUnit);
    if (n * factor > batch.quantity) { toast.error(`Only ${Math.floor(batch.quantity / factor)} ${selectedUnit}(s) available.`); return; }
    if (cart.some((l) => l.batch_id === batch.batch_id)) { toast.error("That batch is already on the bill — change its quantity instead."); return; }
    const rate = (batch.selling_price || batch.mrp) * factor;
    const gross = rate * n;
    const discount = 0; // per-line discounts removed; only overall bill discount is supported
    const taxable = gross - discount;
    setCart([...cart, { batch_id: batch.batch_id, batch_no: batch.batch_no, name: picked.name,
      expiry_date: batch.expiry_date, mrp: batch.mrp * factor, rate, quantity: n,
      available: Math.floor(batch.quantity / factor), sale_unit: selectedUnit,
      gst_percent: batch.gst_percent, discount, taxable, gst: taxable * batch.gst_percent / 100,
      line_total: taxable + taxable * batch.gst_percent / 100 }]);
    setPicked(null); setQ(""); setResults([]); setQty("1"); setSaleUnit("unit");
  };

  const reset = () => {
    setCart([]); setBillDiscount("0"); setPayments([{ mode: "cash", amount: "", reference: "" }]);
    setCustomer({ customer_id: "", name: "", phone: "" });
    setReferralId("");
    setMeta({ doctor_name: "", invoice_date: today(), due_date: "", notes: "" });
    setFormError("");
  };

  // per-line discounts removed: only overall bill discount applies

  const saveEdit = (idx) => {
    const n = Number(editingQty || 0);
    if (n <= 0) { toast.error("Quantity must be at least 1."); return; }
    const b = cart[idx];
    if (n > (b.available || 0)) { toast.error(`Only ${b.available} units available in batch ${b.batch_no}.`); return; }
    const rate = b.rate;
    const gross = rate * n;
    const discount = 0;
    const taxable = gross - discount;
    const updated = { ...b, quantity: n, discount, taxable, gst: taxable * b.gst_percent / 100,
      line_total: taxable + taxable * b.gst_percent / 100 };
    setCart(cart.map((it, j) => j === idx ? updated : it));
    setEditingIdx(null); setEditingQty("1");
  };

  const save = async () => {
    setFormError("");
    if (!cart.length) { setFormError("Add at least one product to the bill."); return; }
    if (!customer.name.trim()) { setFormError("Enter or select a customer for this bill."); return; }
    const paid = payments.filter((p) => money(p.amount) > 0)
      .map((p) => ({ mode: p.mode, amount: money(p.amount), reference: p.reference }));
    if (paid.length && Math.abs(totals.balance) > 0.01 && totals.balance > 0) {
      setFormError(`Collected ${inr(totals.collected)} is less than the grand total ${inr(totals.grand)}.`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/pharmacy/sales", {
        items: cart.map((l) => ({ batch_id: l.batch_id, quantity: l.quantity, sale_unit: l.sale_unit, discount_amount: 0 })),
        customer_id: customer.customer_id, customer_name: customer.name.trim(),
        customer_phone: customer.phone, doctor_name: meta.doctor_name, referral_id: referralId,
        invoice_date: meta.invoice_date, due_date: meta.due_date, notes: meta.notes,
        discount_mode: discountMode, overall_discount: money(billDiscount),
        payments: paid, payment_mode: paid[0]?.mode || "cash", terms });
      toast.success(`Invoice ${data.sale.invoice_no} saved — ${inr(data.sale.total)}`);
      setPreview({ sale: data.sale, business: data.business });
      reset();
    } catch (e) { setFormError(apiError(e)); toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const saveInvoiceSettings = async (patch = {}) => {
    if (!can("settings.manage")) return;
    try { await api.post("/pharmacy/invoice-settings", { template, terms, ...patch }); }
    catch { /* the selection still applies to this print */ }
  };

  const saveTemplateDefault = (value) => {
    setTemplate(value);
    saveInvoiceSettings({ template: value });
  };

  return (
    <div data-testid="pos-page">
      <PageHeader overline="Pharmacy" title="POS billing"
        subtitle="Scan or search a product, build the bill, split the payment and print. Totals are always recomputed and stored by the server."
        actions={<>
          <ScanButton onClick={() => setScanOpen(true)} label="Scan barcode" testid="pos-scan-btn" />
          <button onClick={reset} className={ghostBtnCls} data-testid="pos-clear-btn">Clear bill</button>
          <button onClick={save} disabled={busy} className={primaryBtnCls} data-testid="pos-save-btn">Save &amp; preview</button>
        </>} />

      <div className="grid gap-6 xl:grid-cols-[1.55fr_1fr]">
        <div className="space-y-6">
          <div className="panel p-5">
            <p className="overline text-[#EA580C] mb-4">Customer &amp; invoice</p>
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="Customer">
                <div className="flex gap-2">
                  <input className={inputCls} value={customer.name} data-testid="pos-customer-input"
                    placeholder="Search patient / past customer, or type a name"
                    onChange={(e) => { setCustomer({ ...customer, customer_id: "", name: e.target.value }); searchCustomers(e.target.value); }} />
                  <button onClick={() => { setCustForm({ name: "", phone: "" }); setCustOpen(true); }}
                    className={ghostBtnCls} data-testid="pos-add-customer-btn" title="Create customer">
                    <UserPlus className="h-4 w-4" />
                  </button>
                </div>
                {custResults.length > 0 && (
                  <div className="mt-2 divide-y divide-slate-100 rounded-lg border border-slate-200"
                    data-testid="pos-customer-results">
                    {custResults.map((c, i) => (
                      <button key={`${c.customer_id}-${i}`} type="button"
                        data-testid={`pos-customer-result-${i}`}
                        onClick={() => { setCustomer({ customer_id: c.customer_id, name: c.name, phone: c.phone }); setCustResults([]); }}
                        className="flex w-full items-center justify-between gap-3 p-2.5 text-left text-sm hover:bg-slate-50">
                        <span>
                          <span className="block font-medium text-slate-900">{c.name}</span>
                          <span className="block text-xs text-slate-500">
                            {[c.phone, c.uhid && `UHID ${c.uhid}`].filter(Boolean).join(" · ") || "No mobile on record"}
                          </span>
                        </span>
                        <span className="text-[10px] uppercase tracking-wider text-slate-400">{c.source}</span>
                      </button>
                    ))}
                  </div>
                )}
              </Field>
              <PhoneInput label="Customer mobile" value={customer.phone} testid="pos-customer-phone"
                onChange={(v) => setCustomer({ ...customer, phone: v })} />
              <Field label="Doctor / reference">
                <input className={inputCls} value={meta.doctor_name} data-testid="pos-doctor-input"
                  onChange={(e) => setMeta({ ...meta, doctor_name: e.target.value })} />
              </Field>
              <Field label="Referral doctor / hospital">
                <select className={inputCls} value={referralId} data-testid="pos-referral-select"
                  onChange={(e) => setReferralId(e.target.value)}>
                  <option value="">No referral</option>
                  {referrals.map((r) => (
                    <option key={r.referral_id} value={r.referral_id}>
                      {r.name} · {r.percentage}%
                    </option>
                  ))}
                </select>
                {referralId && (
                  <p className="mt-2 text-xs text-slate-500" data-testid="pos-referral-hint">
                    Referral amount ≈ {inr(totals.grand
                      * (referrals.find((r) => r.referral_id === referralId)?.percentage || 0) / 100)}
                    {" "}on the net bill after discount.
                  </p>
                )}
              </Field>
              <Field label="Invoice no.">
                <input className={inputCls} value="Auto-generated on save" readOnly data-testid="pos-invoice-no" />
              </Field>
              <Field label="Invoice date">
                <input type="date" className={inputCls} value={meta.invoice_date} data-testid="pos-invoice-date"
                  onChange={(e) => setMeta({ ...meta, invoice_date: e.target.value })} />
              </Field>
              <Field label="Due date">
                <input type="date" className={inputCls} value={meta.due_date} data-testid="pos-due-date"
                  onChange={(e) => setMeta({ ...meta, due_date: e.target.value })} />
              </Field>
            </div>
          </div>

          <div className="panel p-5">
            <p className="overline text-[#EA580C] mb-4">Add product</p>
            <div className="grid gap-4 sm:grid-cols-[1.6fr_0.6fr_0.7fr_auto] sm:items-end">
              <Field label="Barcode / product code / name">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input className={`${inputCls} pl-9`} value={q} data-testid="pos-search-input" autoFocus
                    placeholder="Scan barcode, or type code / name"
                    onChange={(e) => search(e.target.value)}
                    // Let Enter select a single result when applicable — avoid global Enter navigation.
                    data-enter-next="false"
                    onKeyDown={(e) => { if (e.key === "Enter" && results.length === 1) {
                      const next = results[0];
                      setPicked(next);
                      setSaleUnit(baseUnit(next));
                    } }} />
                </div>
              </Field>
              <Field label="Quantity">
                <input type="number" min="1" className={inputCls} value={qty} data-testid="pos-qty-input"
                  onChange={(e) => setQty(e.target.value)} />
              </Field>
              <Field label="Sell as">
                <select className={inputCls} value={saleUnit} data-testid="pos-sale-unit-select"
                  onChange={(e) => setSaleUnit(e.target.value)}>
                  {productPackaging(picked).map((unit) => (
                    <option key={unit.code} value={unit.code}>{unit.label}</option>
                  ))}
                  {!picked && <option value="unit">Unit</option>}
                </select>
              </Field>
              {/* Per-line discounts removed — use the Bill summary discount only */}
              <button onClick={addLine} className={primaryBtnCls} data-testid="pos-add-to-bill-btn">
                <Plus className="h-4 w-4" /> Add to bill
              </button>
            </div>
            {/* Per-line discount controls removed to avoid confusion. Use the Bill summary discount. */}

            {picked && (
              <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm" data-testid="pos-selected-product">
                <p className="font-semibold text-slate-900">{picked.name}</p>
                <p className="mt-1 text-xs text-slate-700">
                  Batch {picked.batches?.[0]?.batch_no} · Expiry {picked.batches?.[0]?.expiry_date ? dmy(picked.batches[0].expiry_date) : "—"}
                  {" "}· MRP {inr(picked.batches?.[0]?.mrp)} · Rate {inr(picked.batches?.[0]?.selling_price || picked.batches?.[0]?.mrp)}
                  {" "}· GST {picked.batches?.[0]?.gst_percent}%
                </p>
                <p className="mt-1 text-xs font-medium text-emerald-800" data-testid="pos-base-stock-hint">
                  Stock is tracked by base units: {stockHint(picked, picked.batches?.[0])}.
                  {" "}Selected {saleUnit || baseUnit(picked)} uses {sellFactor(picked, saleUnit || baseUnit(picked))} base unit(s).
                </p>
              </div>
            )}
            {!picked && results.length > 0 && (
              <div className="mt-4 divide-y divide-slate-100 rounded-lg border border-slate-200" data-testid="pos-search-results">
                {results.map((m) => (
                  <div key={(m.medicine_id || m.master_id) + String(!!m._is_master)} className="flex w-full items-center justify-between gap-4 p-3.5 hover:bg-slate-50">
                    <div className="min-w-0 text-left">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-medium text-slate-900 truncate">
                          {m.code ? <span className="font-mono-num mr-2 text-slate-500">{m.code}</span> : null}{m.name}
                        </div>
                        <div className="text-sm font-medium">{inr(m.batches?.[0]?.selling_price || m.batches?.[0]?.mrp || m.mrp || 0)}</div>
                      </div>
                      <div className="text-xs text-slate-600 mt-1 truncate">
                        {m._is_master ? (m.generic_name || "Master catalog item — import before billing") : (m.batches?.length ? `Batch ${m.batches[0].batch_no} · Exp ${dmy(m.batches[0].expiry_date)} · ${m.batches[0].quantity} left` : "No sellable stock — cannot bill from this suggestion")}
                      </div>
                    </div>
                    <div>
                      {m._is_master ? (
                        <button className={ghostBtnCls} onClick={async () => {
                          try {
                            const { data } = await api.post('/pharmacy/medicines/import-master', { master_id: m.master_id });
                            toast.success(`${data.medicine.name} imported to your business.`);
                            // Refresh search so the newly imported tenant medicine (if any batches later) appears.
                            search(q);
                          } catch (err) { toast.error(apiError(err) || 'Could not import product.'); }
                        }} data-testid={`pos-import-master-${m.master_id}`}>
                          Add to business
                        </button>
                      ) : (
                        <button onClick={() => {
                          setPicked(m);
                          setSaleUnit(baseUnit(m));
                        }} data-testid={`pos-result-${m.medicine_id}`}
                          className="text-slate-700">Select</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="panel overflow-hidden" data-testid="pos-cart">
            {cart.length === 0 ? (
              <EmptyState title="No products on this bill" subtitle="Scan a barcode or search a medicine to start billing." testid="pos-cart-empty" />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="px-4 py-3">Product</th><th className="px-3 py-3">Batch</th>
                      <th className="px-3 py-3">Expiry</th><th className="px-3 py-3 text-right">MRP</th>
                      <th className="px-3 py-3 text-right">Rate</th><th className="px-3 py-3 text-right">Qty</th>
                      <th className="px-3 py-3 text-right">GST</th>
                      <th className="px-3 py-3 text-right">Total</th><th className="px-3 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {cart.map((l, i) => (
                      <tr key={l.batch_id} data-testid={`pos-line-${i}`}>
                        <td className="px-4 py-3 font-medium text-slate-900">{l.name}</td>
                        <td className="px-3 py-3 text-slate-600">{l.batch_no}</td>
                        <td className="px-3 py-3 text-slate-600">{dmy(l.expiry_date)}</td>
                        <td className="px-3 py-3 text-right text-slate-600">{inr(l.mrp)}</td>
                        <td className="px-3 py-3 text-right text-slate-600">{inr(l.rate)}</td>
                        <td className="px-3 py-3 text-right">
                          {editingIdx === i ? (
                            <div className="flex items-center justify-end gap-2">
                              <input type="number" min="1" className={`${inputCls} !w-20`} value={editingQty}
                                onChange={(e) => setEditingQty(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") saveEdit(i); }} />
                              <button onClick={() => saveEdit(i)} className={ghostBtnCls}>Save</button>
                              <button onClick={() => setEditingIdx(null)} className="text-slate-500">Cancel</button>
                            </div>
                          ) : (
                            <div className="flex items-center justify-end gap-2">
                              <span>{l.quantity} {l.sale_unit}</span>
                              <button onClick={() => { setEditingIdx(i); setEditingQty(String(l.quantity)); }}
                                className="text-slate-600" aria-label="Edit quantity"><Edit2 className="h-4 w-4" /></button>
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-3 text-right text-slate-600">{l.gst_percent}%</td>
                        <td className="px-3 py-3 text-right font-medium" data-testid={`pos-line-total-${i}`}>{inr(l.line_total)}</td>
                        <td className="px-3 py-3 text-right">
                          <button onClick={() => setCart(cart.filter((_, x) => x !== i))}
                            data-testid={`pos-remove-line-${i}`} aria-label="Remove line"
                            className="text-red-600 hover:text-red-800"><Trash2 className="h-4 w-4" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="panel p-5">
            <p className="overline text-[#EA580C] mb-4">Bill summary</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Discount type">
                <select className={inputCls} value={discountMode} data-testid="pos-discount-mode"
                  onChange={(e) => setDiscountMode(e.target.value)}>
                  <option value="amount" label="Fixed ₹ amount" />
                  <option value="percent" label="Percentage %" />
                </select>
              </Field>
              <Field label={discountMode === "percent" ? "Bill discount %" : "Bill discount ₹"}>
                <input type="number" min="0" className={inputCls} value={billDiscount}
                  data-testid="pos-bill-discount-input" onChange={(e) => setBillDiscount(e.target.value)} />
              </Field>
            </div>
            <div className="mt-5 space-y-1.5 text-sm">
              {[
                ["Taxable amount", inr(totals.subtotal), "pos-subtotal"],
                ["Total tax (GST)", inr(totals.gst), "pos-gst"],
                ["Bill discount", `- ${inr(totals.billDisc)}`, "pos-bill-discount"],
                ["Round off", inr(totals.roundOff), "pos-round-off"],
              ].map(([k, v, t]) => (
                <div key={k} className="flex justify-between"><span className="text-slate-600">{k}</span><span data-testid={t}>{v}</span></div>
              ))}
              <div className="mt-2 flex items-baseline justify-between border-t-2 border-slate-900 pt-2.5">
                <span className="font-ui font-bold">Grand total</span>
                <span className="font-ui text-2xl font-bold" data-testid="pos-grand-total">{inr(totals.grand)}</span>
              </div>
            </div>
          </div>

          <div className="panel p-5">
            <div className="mb-4 flex items-center justify-between">
              <p className="overline text-[#EA580C]">Split payment</p>
              <button onClick={() => setPayments([...payments, { mode: "upi", amount: "", reference: "" }])}
                className={ghostBtnCls} data-testid="pos-add-payment-btn"><Plus className="h-3.5 w-3.5" /> Add mode</button>
            </div>
            <div className="space-y-3">
              {payments.map((p, i) => (
                <div key={i} className="grid gap-3 sm:grid-cols-[0.9fr_0.8fr_1fr_auto] sm:items-center" data-testid={`pos-payment-${i}`}>
                  <select className={inputCls} value={p.mode} data-testid={`pos-payment-mode-${i}`}
                    onChange={(e) => setPayments(payments.map((x, k) => k === i ? { ...x, mode: e.target.value } : x))}>
                    {MODES.map((m) => <option key={m} value={m} label={titleize(m)} />)}
                  </select>
                  <input type="number" min="0" placeholder="Amount" className={inputCls} value={p.amount}
                    data-testid={`pos-payment-amount-${i}`}
                    onChange={(e) => setPayments(payments.map((x, k) => k === i ? { ...x, amount: e.target.value } : x))} />
                  <input placeholder="Reference (optional)" className={inputCls} value={p.reference}
                    onChange={(e) => setPayments(payments.map((x, k) => k === i ? { ...x, reference: e.target.value } : x))} />
                  {payments.length > 1 && (
                    <button onClick={() => setPayments(payments.filter((_, k) => k !== i))}
                      data-testid={`pos-remove-payment-${i}`} aria-label="Remove payment mode"
                      className="text-red-600 hover:text-red-800"><Trash2 className="h-4 w-4" /></button>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-4 flex justify-between text-sm">
              <span className="text-slate-600">Collected</span>
              <span data-testid="pos-collected">{inr(totals.collected)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">
                {payments.every((p) => !money(p.amount))
                  ? "Full amount will be collected in the first mode"
                  : totals.balance > 0 ? "Balance due" : "Change to return"}
              </span>
              <span className={payments.some((p) => money(p.amount)) && totals.balance > 0 ? "text-red-600" : ""}
                data-testid="pos-balance">
                {payments.every((p) => !money(p.amount)) ? inr(totals.grand) : inr(Math.abs(totals.balance))}
              </span>
            </div>
            <p className="mt-2 text-xs text-slate-500">Leave the amount blank to collect the full total in the first mode.</p>
          </div>

          <div className="panel p-5">
            <p className="overline text-[#EA580C] mb-4">Invoice template &amp; terms</p>
            <Field label="Template (saved as the business default)">
              <select className={inputCls} value={template} data-testid="pos-template-select"
                onChange={(e) => saveTemplateDefault(e.target.value)}>
                {TEMPLATES.map((t) => <option key={t.value} value={t.value} label={t.label} />)}
              </select>
            </Field>
            <p className="mt-2 text-xs text-slate-500">{TEMPLATES.find((t) => t.value === template)?.hint}</p>
            <Field label="Terms & conditions (printed on the invoice)" className="mt-4">
              <textarea rows={3} className={inputCls} value={terms} data-testid="pos-terms-input"
                placeholder="Goods once sold are not returnable without a valid prescription…"
                onBlur={() => saveInvoiceSettings()}
                onChange={(e) => setTerms(e.target.value)} />
            </Field>
          </div>

          {formError && <p className="text-sm font-medium text-red-600" data-testid="pos-error">{formError}</p>}
          <button onClick={save} disabled={busy || !cart.length} className={`${primaryBtnCls} w-full`}
            data-testid="pos-checkout-btn">
            <Printer className="h-4 w-4" /> Save bill &amp; open invoice
          </button>
        </div>
      </div>

      <Modal open={custOpen} onClose={() => setCustOpen(false)} title="Create customer" testid="pos-customer-modal">
        <form className="grid gap-5 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (!custForm.name.trim()) { toast.error("Customer name is required."); return; }
            // Persist customer to backend and use returned id
            (async () => {
              try {
                const { data } = await api.post('/pharmacy/customers', { name: custForm.name.trim(), phone: custForm.phone });
                const c = data.customer;
                setCustomer({ customer_id: c.customer_id || "", name: c.name || custForm.name.trim(), phone: c.phone || custForm.phone });
                setCustOpen(false);
                toast.success("Customer saved and added to this bill.");
              } catch (err) {
                toast.error(apiError(err) || 'Could not save customer.');
              }
            })();
          }}>
          <Field label="Customer name *">
            <input className={inputCls} value={custForm.name} data-testid="pos-new-customer-name"
              onChange={(e) => setCustForm({ ...custForm, name: e.target.value })} />
          </Field>
          <PhoneInput label="Mobile" value={custForm.phone} testid="pos-new-customer-phone"
            onChange={(v) => setCustForm({ ...custForm, phone: v })} />
          <div className="sm:col-span-2">
            <button className={primaryBtnCls} data-testid="pos-save-customer-btn">Use this customer</button>
          </div>
        </form>
      </Modal>

      <PrintPreview open={Boolean(preview)} onClose={() => setPreview(null)}
        title={`Invoice ${preview?.sale?.invoice_no || ""}`}
        subtitle={`${TEMPLATES.find((t) => t.value === template)?.label} template`}
        testid="pos-invoice-preview">
        <PharmacyInvoiceDoc sale={preview?.sale} business={preview?.business} template={template} />
      </PrintPreview>

      {scanOpen && (
        <BarcodeScanner testid="pos-scanner"
          onScan={(code) => handleBarcode(code)}
          onClose={() => setScanOpen(false)} />
      )}
    </div>
  );
}
